import type { FC } from 'react';
import React from 'react';
import {
  Box,
  Button,
  Card,
  Checkbox,
  FormControlLabel,
  Stack,
  Typography,
} from '@mui/material';

import { DatePicker } from '@mui/x-date-pickers';

interface ResidentPdfExportModalViewProps {
  onClose: () => void;
  onExport: () => void;
  onLastMonth: () => void;
  onLastYear: () => void;

  // Form state
  dateFrom: Date | null;
  dateTo: Date | null;
  onDateFromChange: (date: Date | null) => void;
  onDateToChange: (date: Date | null) => void;

  includeReceiptImages: boolean;
  onIncludeReceiptImagesChange: (checked: boolean) => void;

  includeCancelledReceiptAttachments: boolean;
  onIncludeCancelledReceiptAttachmentsChange: (checked: boolean) => void;

  // State
  isLoadingTransactions: boolean;
  preparingPDF: boolean;
  isImageLoading: boolean;

  // Configuration
  residentCreatedDate: Date;
  lastMonthButtonActive: boolean;
  lastYearButtonActive: boolean;
  residentCreatedDateOlderThanCurrentMonth: boolean;
  residentCreatedDateOlderThanCurrentYear: boolean;
  hasCancelledReceipts: boolean;
  showCancelledReceiptsCheckbox?: boolean;

  // Validation
  dateError?: string;

  // Text labels
  title: string;
  pleaseSelectRange: string;
  fromLabel: string;
  toLabel: string;
  lastMonthLabel: string;
  lastYearLabel: string;
  receiptOptionsLabel: string;
  includeReceiptImagesLabel: string;
  includeCancelledReceiptAttachmentsLabel: string;
  cancelLabel: string;
  exportLabel: string;
}

export const ResidentPdfExportModalView: FC<
  ResidentPdfExportModalViewProps
> = ({
  onClose,
  onExport,
  onLastMonth,
  onLastYear,
  dateFrom,
  dateTo,
  onDateFromChange,
  onDateToChange,
  includeReceiptImages,
  onIncludeReceiptImagesChange,
  includeCancelledReceiptAttachments,
  onIncludeCancelledReceiptAttachmentsChange,
  isLoadingTransactions,
  preparingPDF,
  isImageLoading,
  residentCreatedDate,
  lastMonthButtonActive,
  lastYearButtonActive,
  residentCreatedDateOlderThanCurrentMonth,
  residentCreatedDateOlderThanCurrentYear,
  hasCancelledReceipts,
  showCancelledReceiptsCheckbox,
  dateError,
  title,
  pleaseSelectRange,
  fromLabel,
  toLabel,
  lastMonthLabel,
  lastYearLabel,
  receiptOptionsLabel,
  includeReceiptImagesLabel,
  includeCancelledReceiptAttachmentsLabel,
  cancelLabel,
  exportLabel,
}) => {
  return (
    <Box>
      <Card sx={{ p: 3 }}>
        <Typography
          variant="h5"
          sx={{
            fontWeight: 700,
            mb: 1,
          }}
        >
          {title}
        </Typography>
        <Typography sx={{ mb: 1 }}>{pleaseSelectRange}:</Typography>
        <Stack
          direction="row"
          sx={{
            justifyContent: 'flex-end',
            gap: 1,
          }}
        >
          <DatePicker
            label={fromLabel}
            value={dateFrom}
            onChange={onDateFromChange}
            minDate={residentCreatedDate}
            maxDate={new Date()}
            slotProps={{
              textField: {
                label: fromLabel,
                helperText: dateError,
                error: dateError ? true : false,
              },
            }}
          />
          <DatePicker
            label={toLabel}
            value={dateTo}
            onChange={onDateToChange}
            minDate={residentCreatedDate}
            maxDate={new Date()}
            slotProps={{
              textField: {
                label: toLabel,
                error: dateError ? true : false,
              },
            }}
          />
        </Stack>

        <Stack
          direction="row"
          sx={{
            justifyContent: 'flex-start',
            gap: 1,
            mt: 1,
            mb: 3,
          }}
        >
          <Button
            disabled={!residentCreatedDateOlderThanCurrentMonth}
            onClick={onLastMonth}
            variant={lastMonthButtonActive ? 'contained' : 'outlined'}
            size="small"
          >
            {lastMonthLabel}
          </Button>
          <Button
            disabled={!residentCreatedDateOlderThanCurrentYear}
            onClick={onLastYear}
            variant={lastYearButtonActive ? 'contained' : 'outlined'}
            size="small"
          >
            {lastYearLabel}
          </Button>
        </Stack>
        <Typography>{receiptOptionsLabel}</Typography>
        <FormControlLabel
          control={
            <Checkbox
              checked={includeReceiptImages}
              onChange={(e, checked) => onIncludeReceiptImagesChange(checked)}
            />
          }
          label={includeReceiptImagesLabel}
        />
        {includeReceiptImages &&
          hasCancelledReceipts &&
          showCancelledReceiptsCheckbox && (
            <FormControlLabel
              control={
                <Checkbox
                  checked={includeCancelledReceiptAttachments}
                  onChange={(e, checked) =>
                    onIncludeCancelledReceiptAttachmentsChange(checked)
                  }
                />
              }
              label={includeCancelledReceiptAttachmentsLabel}
            />
          )}

        <Stack
          direction="row"
          sx={{
            justifyContent: 'flex-end',
            gap: 1,
          }}
        >
          <Button variant="outlined" disabled={preparingPDF} onClick={onClose}>
            {cancelLabel}
          </Button>

          <Button
            color="primary"
            variant="contained"
            loading={preparingPDF || isLoadingTransactions || isImageLoading}
            disabled={
              preparingPDF || Boolean(dateError) || !dateFrom || !dateTo
            }
            onClick={onExport}
          >
            {exportLabel}
          </Button>
        </Stack>
      </Card>
    </Box>
  );
};
